/**
 * NxLearningEngine -- Standalone quiz/learning engine.
 * Extracted from PublicidadView.js NSS5 Activity Engine.
 * Can load questions from API (with fallback to local QUIZ_BANK).
 * Tracks progress via API + localStorage dual-write.
 *
 * @module presentation/shared/NxLearningEngine
 * @version 1.0
 */

import { LearningApiClient } from '../../infrastructure/api/LearningApiClient.js';

export const NX_LEARNING_ENGINE_VERSION = '1.0';

const NSS5_STORAGE_KEY = 'nss5_quiz_progress';
// Deuda 4: el escape local no cambiaba & ni " y perdia el 0; ahora es el compartido.
import { esc } from './texto.js';

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function loadLocalProgress(userId) {
  try {
    const raw = localStorage.getItem(NSS5_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw)[userId] ?? null;
  } catch { return null; }
}

function saveLocalProgress(userId, data) {
  try {
    const raw = localStorage.getItem(NSS5_STORAGE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[userId] = { ...data, updatedAt: Date.now() };
    localStorage.setItem(NSS5_STORAGE_KEY, JSON.stringify(all));
  } catch {}
}

/**
 * Build and mount the learning quiz engine.
 *
 * @param {HTMLElement} containerEl - DOM element to render into
 * @param {Object} config
 * @param {string} [config.userId] - User ID for progress tracking
 * @param {string} [config.category] - Filter questions by category
 * @param {Array}  [config.fallbackQuestions] - Local questions if API fails
 * @param {string} [config.context] - Context label (e.g. 'publicidad', 'capacitacion')
 * @param {boolean} [config.immediateFeedback] - Muestra el "por que" tras cada
 *        respuesta y espera a "Siguiente" (cursos). Sin esto, autoavanza 1.4s.
 * @param {Function} [config.onComplete] - Called when user finishes a round
 * @returns {{ destroy: Function }}
 */
export async function buildLearningEngine(containerEl, config = {}) {
  const uid = config.userId || 'anon';
  const context = config.context || 'publicidad';

  // Load questions from API with fallback
  let questions = [];
  try {
    const res = await LearningApiClient.getQuestions({
      category: config.category,
      limit: 41,
      random: true
    });
    if (res.ok && res.data?.data?.length > 0) {
      questions = res.data.data.map(q => ({
        id: q.id,
        q: q.question_text || q.questionText,
        opts: typeof q.options === 'string' ? JSON.parse(q.options) : q.options,
        a: q.correct_index ?? q.correctIndex,
        cat: q.category,
        // Retroalimentacion inmediata (SKILL-USO): el "por que" de la respuesta.
        expl: q.explanation || q.explicacion || ''
      }));
    }
  } catch {}

  // Fallback to local questions
  if (questions.length === 0 && config.fallbackQuestions) {
    questions = shuffleArray(config.fallbackQuestions);
  }

  if (questions.length === 0) {
    containerEl.innerHTML = '<p class="empty-state">Sin preguntas disponibles</p>';
    return { destroy() { containerEl.innerHTML = ''; } };
  }

  questions = shuffleArray(questions);
  const immediateFeedback = !!config.immediateFeedback;

  const saved = loadLocalProgress(uid);
  let idx = 0;
  let score = saved?.score ?? 0;
  let answered = saved?.answered ?? 0;
  let selectedOpt = null;

  function persist() {
    saveLocalProgress(uid, { score, answered });
  }

  function catLabel(cat) {
    return cat === 'productividad' ? '\uD83D\uDCCB Productividad' : '\uD83D\uDCE3 Marketing Digital';
  }

  function renderQuestion() {
    if (idx >= questions.length) {
      idx = 0;
      if (config.onComplete) config.onComplete({ score, answered });
    }
    const q = questions[idx];
    selectedOpt = null;
    containerEl.innerHTML = `
      <div class="nss5-quiz-wrap">
        <div class="nss5-quiz-header">
          <span class="nss5-quiz-badge">Capacitacion Nexo</span>
          <span class="nss5-quiz-score">${score}/${answered} correctas</span>
        </div>
        <div class="nss5-quiz-question">${esc(q.q)}</div>
        <div class="nss5-quiz-opts">
          ${q.opts.map((o, i) => `
            <button type="button" class="nss5-quiz-opt" data-idx="${i}">${esc(o)}</button>
          `).join('')}
        </div>
        <div class="nss5-quiz-footer">
          <span class="nss5-quiz-cat">${catLabel(q.cat)}</span>
          <span class="nss5-quiz-progress">Pregunta ${answered + 1}</span>
        </div>
      </div>`;
    containerEl.querySelectorAll('.nss5-quiz-opt').forEach(btn => {
      btn.addEventListener('click', () => handleAnswer(btn, q));
    });
  }

  function handleAnswer(btn, q) {
    if (selectedOpt !== null) return;
    const chosen = parseInt(btn.dataset.idx, 10);
    selectedOpt = chosen;
    answered++;
    const correct = chosen === q.a;
    if (correct) score++;
    persist();

    // Record progress to API (fire and forget)
    if (q.id) {
      LearningApiClient.recordProgress({
        userId: uid,
        questionId: q.id,
        isCorrect: correct,
        context
      }).catch(() => {});
    }

    containerEl.querySelectorAll('.nss5-quiz-opt').forEach((b, i) => {
      b.classList.add('nss5-quiz-opt--locked');
      if (i === q.a) b.classList.add('nss5-quiz-opt--correct');
      if (i === chosen && !correct) b.classList.add('nss5-quiz-opt--wrong');
    });
    const scoreEl = containerEl.querySelector('.nss5-quiz-score');
    if (scoreEl) scoreEl.textContent = `${score}/${answered} correctas`;

    // RETROALIMENTACION INMEDIATA (opt-in via config.immediateFeedback): en vez
    // de saltar solo a los 1.4s, muestra si acerto + el "por que" y espera a
    // que el usuario lea y pulse Siguiente. Es la base del curso de primer uso
    // de Desktop (SKILL-USO): aprender del error, no solo marcarlo.
    if (immediateFeedback) {
      const fb = document.createElement('div');
      fb.className = `nss5-quiz-feedback ${correct ? 'nss5-quiz-feedback--ok' : 'nss5-quiz-feedback--no'}`;
      fb.innerHTML = `
        <div class="nss5-quiz-feedback-head">${correct ? '&#10003; ¡Correcto!' : '&#10007; Casi. Mira por qué:'}</div>
        ${q.expl ? `<div class="nss5-quiz-feedback-body">${esc(q.expl)}</div>` : ''}
        <button type="button" class="nss5-quiz-next">${idx + 1 < questions.length ? 'Siguiente &rarr;' : 'Ver resultado'}</button>`;
      const wrap = containerEl.querySelector('.nss5-quiz-wrap');
      if (wrap) wrap.appendChild(fb);
      const nextBtn = fb.querySelector('.nss5-quiz-next');
      if (nextBtn) nextBtn.addEventListener('click', () => { idx++; renderQuestion(); });
      return;
    }
    setTimeout(() => { idx++; renderQuestion(); }, 1400);
  }

  renderQuestion();
  return { destroy() { containerEl.innerHTML = ''; } };
}
